import type { BadgeTone } from "../../components/ui/badge";

export type CallStatus = "completed" | "failed";
export type Sentiment = "positive" | "negative" | "neutral";
export type CallDirection = "inbound" | "outbound";
export type CallSearchField =
  | "all" | "contact_id" | "phone" | "schedule_id" | "execution_id" | "agent" | "campaign";

export interface CallOutcome { field: string; value: string }
export interface TranscriptLine {
  speaker: "agent" | "contact";
  text: string;
  at: string;
  /**
   * The part of `text` that actually reached the speaker, when the caller cut
   * the agent off partway through. Absent means the turn played through in full,
   * or the call predates the worker measuring cuts -- either way, show `text`.
   * An empty string is meaningful: the turn was cut before any audio was voiced.
   */
  spokenPrefix?: string;
}

export interface CallRecord {
  executionId: string;
  contactId: string;
  phone: string;
  direction?: CallDirection;
  destinationDid?: string;
  scheduleId: string;
  agent: string;
  agentId?: string;
  workflowId?: string;
  version: string;
  voice: string;
  durationSec: number;
  createdAt: string;
  status: CallStatus;
  hungBy: "user" | "agent" | "system";
  campaign: string;
  sentiment: Sentiment;
  retryable: boolean;
  isTestCall?: boolean;
  isScheduledCall?: boolean;
  outcomes: CallOutcome[];
  transcript: TranscriptLine[];
  aiSummary: string[];
  recordingUrl?: string; // populate from API
  /** The real call id (never the random executionId fallback); recording signing needs it. */
  callId?: string;
}

export const DIRECTION_META: Record<CallDirection, { label: string; tone: BadgeTone }> = {
  inbound:  { label: "Inbound", tone: "brand" },
  outbound: { label: "Outbound", tone: "neutral" },
};

export const DIRECTION_OPTIONS = [
  { value: "all", label: "All Directions" },
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
] as const;
export type CallDirectionFilter = (typeof DIRECTION_OPTIONS)[number]["value"];

export const STATUS_META: Record<CallStatus, { label: string; tone: BadgeTone }> = {
  completed: { label: "Completed", tone: "success" },
  failed:    { label: "Failed", tone: "danger" },
};

export const SENTIMENT_META: Record<Sentiment, { label: string; tone: BadgeTone }> = {
  positive: { label: "Positive", tone: "success" },
  negative: { label: "Negative", tone: "danger" },
  neutral:  { label: "Neutral", tone: "neutral" },
};

export const SEARCH_FIELDS: ReadonlyArray<{ value: CallSearchField; label: string }> = [
  { value: "all", label: "All Fields" },
  { value: "contact_id", label: "Contact ID" },
  { value: "phone", label: "Customer Phone" },
  { value: "schedule_id", label: "Schedule ID" },
  { value: "execution_id", label: "Execution ID" },
  { value: "agent", label: "Agent" },
  { value: "campaign", label: "Campaign" },
];

export const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
] as const;
export type CallStatusFilter = (typeof STATUS_OPTIONS)[number]["value"];
